import { useEffect, useRef } from "react";

interface LeaveWithoutSavingDialogProps {
  isRetrying: boolean;
  onStay: () => void;
  onRetry: () => void;
  onLeave: () => void;
}

export function LeaveWithoutSavingDialog({
  isRetrying,
  onStay,
  onRetry,
  onLeave,
}: LeaveWithoutSavingDialogProps) {
  const stayButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    stayButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !isRetrying) onStay();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isRetrying, onStay]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={isRetrying ? undefined : onStay}
      role="presentation"
    >
      <section
        aria-describedby="leave-save-description"
        aria-labelledby="leave-save-title"
        aria-modal="true"
        className="confirm-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <h2 id="leave-save-title">Your drawing didn’t save</h2>
        <p id="leave-save-description">
          Stay here and retry so your latest art is not lost. Leaving now will discard unsaved
          changes.
        </p>
        <div className="confirm-dialog__actions">
          <button
            className="button button--secondary"
            onClick={onStay}
            ref={stayButtonRef}
            type="button"
          >
            Stay here
          </button>
          <button
            className="button button--primary"
            disabled={isRetrying}
            onClick={onRetry}
            type="button"
          >
            {isRetrying ? "Retrying…" : "Retry save"}
          </button>
          <button
            className="button button--danger"
            disabled={isRetrying}
            onClick={onLeave}
            type="button"
          >
            Leave without saving
          </button>
        </div>
      </section>
    </div>
  );
}
